import React, { useState } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Button,
  useTheme,
  useMediaQuery,
  Container,
  Paper,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Home,
  Logout,
  Visibility,
  ViewCarousel,
  Business,
  Work,
  ContactMail,
  Dashboard as DashboardIcon,
  Info,
  Preview,
  ViewSidebar,
  Web,
  Image,
} from '@mui/icons-material';
import { MainVisualEditor } from './MainVisualEditor';
import { BusinessAreaEditor } from './BusinessAreaEditor';
import { AboutEditor } from './AboutEditor';
import { PortfolioEditor } from './PortfolioEditor';
import { ContentEditor } from './ContentEditor';
import { ProjectTypeEditor } from './ProjectTypeEditor';
import { CompanyInfoEditor } from './CompanyInfoEditor';
import { PortfolioManager } from './PortfolioManager';
import { PortfolioPageEditor } from './PortfolioPageEditor';
import { MediaManagement } from './components/MediaManagement';

interface HomeManagementProps {
  onLogout: () => void;
}

const drawerWidth = 280;

const menuItems = [
  { text: '콘텐츠 관리', icon: <Image />, id: 'media-management' },
  { text: '포트폴리오 페이지', icon: <Work />, id: 'portfolio-page' },
  { text: '대표 포트폴리오', icon: <Work />, id: 'portfolio-manager' },
  { text: '프로젝트 유형 관리', icon: <ContactMail />, id: 'contact' },
  { text: '회사 정보', icon: <Business />, id: 'company-info' },
];

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export const HomeManagement: React.FC<HomeManagementProps> = ({ onLogout }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState('media-management');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuClick = (menuId: string) => {
    if (menuId === 'preview') {
      window.open('/', '_blank');
      return;
    }
    setSelectedMenu(menuId);
    if (isMobile) {
      setMobileOpen(false);
    }
  };



  const renderContent = () => {
    switch (selectedMenu) {
      case 'media-management':
        return <MediaManagement />;
      case 'about':
        return (
          <Box>
            <Typography variant="h4" gutterBottom>
              About 페이지 관리
            </Typography>
            <AboutEditor />
          </Box>
        );
      case 'portfolio-manager':
        return (
          <Box>
            <Typography variant="h4" gutterBottom>
              대표 포트폴리오 관리
            </Typography>
            <PortfolioManager />
          </Box>
        );
      case 'portfolio-page':
        return (
          <Box>
            <Typography variant="h4" gutterBottom>
              포트폴리오 관리
            </Typography>
            <PortfolioPageEditor />
          </Box>
        );
      case 'contact':
        return (
          <Box>
            <Typography variant="h4" gutterBottom>
              프로젝트 유형 관리
            </Typography>
            <Paper sx={{ mt: 3, p: 3 }}>
              <ProjectTypeEditor />
            </Paper>
          </Box>
        );

      case 'company-info':
        return (
          <Box>
            <Typography variant="h4" gutterBottom>
              회사 정보 통합 관리
            </Typography>
            <CompanyInfoEditor />
          </Box>
        );

      default:
        return <MediaManagement />;
    }
  };

  const drawer = (
    <div>
      <Toolbar>
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <DashboardIcon sx={{ mr: 2, color: theme.palette.primary.main }} />
          <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 'bold' }}>
            관리자 페이지
          </Typography>
        </Box>
      </Toolbar>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.id} disablePadding>
            <ListItemButton
              selected={selectedMenu === item.id}
              onClick={() => handleMenuClick(item.id)}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: theme.palette.primary.light,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.light,
                  },
                  '& .MuiListItemIcon-root': {
                    color: theme.palette.primary.main,
                  },
                  '& .MuiListItemText-primary': {
                    color: theme.palette.primary.main,
                    fontWeight: 'bold',
                  },
                },
              }}
            >
              <ListItemIcon>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider sx={{ mt: 'auto' }} />
      <List>
        <ListItem disablePadding>
          <ListItemButton onClick={() => window.open('/', '_blank')}>
            <ListItemIcon>
              <Preview />
            </ListItemIcon>
            <ListItemText primary="미리보기" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton onClick={onLogout}>
            <ListItemIcon>
              <Logout />
            </ListItemIcon>
            <ListItemText primary="로그아웃" />
          </ListItemButton>
        </ListItem>
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          backgroundColor: '#ffffff',
          color: '#333333',
          boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {menuItems.find(item => item.id === selectedMenu)?.text || '홈페이지'} 관리
          </Typography>

        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              backgroundColor: '#fafafa',
              borderRight: '1px solid #e0e0e0',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          backgroundColor: '#f5f5f5',
        }}
      >
        <Toolbar />
        <Container maxWidth="xl">
          <Box
            sx={{
              backgroundColor: '#ffffff',
              borderRadius: 2,
              p: 3,
              boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
              minHeight: 'calc(100vh - 120px)',
            }}
          >
            {renderContent()}
          </Box>
        </Container>
      </Box>
    </Box>
  );
}; 